import { supabase } from '../config/supabase';

/**
 * Announce an event in the festival chat (Slack or Discord).
 *
 * The webhook URL is NOT here. It lives in a Netlify environment variable that
 * only the serverless function can read - a webhook URL is a credential, and
 * anything reachable from this file ends up in the public bundle.
 *
 * We send an event type and a movie title. The message wording is composed
 * server side from a fixed set of events, so this cannot be used to post
 * arbitrary text into the channel.
 *
 * Never throws and never blocks. Chat is a nice-to-have; a missing webhook, a
 * failed request or a logged-out user must not disturb what the user was doing.
 */
const ENDPOINT = '/.netlify/functions/notify';

export const notifyChat = async (type, payload = {}) => {
  try {
    if (!supabase) return { sent: false, reason: 'supabase not configured' };

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      // The function requires a session, so don't bother making the call.
      return { sent: false, reason: 'not signed in' };
    }

    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ type, ...payload })
    });

    if (!res.ok) {
      console.warn(`Chat notification (${type}) returned ${res.status}`);
      return { sent: false, reason: `http ${res.status}` };
    }

    const body = await res.json().catch(() => ({}));
    if (body.skipped) return { sent: false, reason: body.skipped };
    // channelId + messageId together identify the countdown message, so later
    // ticks can edit it in place. Both are produced server side; the client
    // only carries them back and forth.
    return {
      sent: true,
      messageId: body.messageId || null,
      channelId: body.channelId || null,
      channelCreated: Boolean(body.channelCreated),
      channelUrl: body.channelUrl || null
    };
  } catch (err) {
    // Includes the local dev case, where /.netlify/functions/ does not exist.
    console.warn(`Chat notification (${type}) failed:`, err.message);
    return { sent: false, reason: err.message };
  }
};

export default notifyChat;
